import { StatusBadge, Table, TableBody, TableCell, TableHeader, TableRow } from '@catering/ui';
import type { MenuBadgeStatus } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import type { MenuListItem, ServiceType } from '../../../menus/api';
import { formatBasePrice } from '../../../menus/format-base-price';

export interface MenuTableProps {
  items: MenuListItem[];
  serviceTypes: ServiceType[];
  /** Separates the "Menús activos"/"Menús archivados" sections (`proposal.md` risk mitigation 3). */
  archived: boolean;
}

export function MenuTable({ items, serviceTypes, archived }: MenuTableProps): JSX.Element {
  const serviceTypeNames = new Map(
    serviceTypes.map((serviceType) => [serviceType.id, serviceType.name]),
  );

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase text-slate-500">
        {archived ? 'Menús archivados' : 'Menús activos'}
      </h2>

      <Table>
        <caption className="sr-only">{archived ? 'Menús archivados' : 'Menús activos'}</caption>
        <thead>
          <tr>
            <TableHeader>Código</TableHeader>
            <TableHeader>Nombre</TableHeader>
            <TableHeader>Tipo de servicio</TableHeader>
            <TableHeader>Precio</TableHeader>
            <TableHeader>Estado</TableHeader>
          </tr>
        </thead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.code}</TableCell>
              <TableCell>
                <Link
                  to="/menus/$menuId"
                  params={{ menuId: item.id }}
                  className="font-medium text-slate-900 hover:text-primary-700 hover:underline"
                >
                  {item.name}
                </Link>
              </TableCell>
              <TableCell>{serviceTypeNames.get(item.serviceTypeId) ?? '—'}</TableCell>
              <TableCell>{formatBasePrice(item.basePriceCents)}</TableCell>
              <TableCell>
                <StatusBadge domain="menu" status={menuBadgeStatus(item)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

/**
 * `MenuListItem` has no `status` field of its own \u2014 `isActive`/`deletedAt`
 * are the source of truth. The type says `isActive: boolean`, but runtime
 * JSON from a version-skewed backend has no such guarantee; a value outside
 * `true`/`false` falls through to `StatusBadge`'s own unknown-status
 * fallback (neutral, raw value as label) instead of crashing the row.
 */
function menuBadgeStatus(item: MenuListItem): MenuBadgeStatus {
  if (item.deletedAt !== null) return 'ARCHIVED';
  if (item.isActive === true) return 'ACTIVE';
  if (item.isActive === false) return 'INACTIVE';

  return String(item.isActive) as MenuBadgeStatus;
}
